'use client';
import {
  type FundingHistoryOperation,
  formatFiatUnits,
  fundingMethodArtwork,
  fundingSettlementValue,
  fundingSourcePresentation,
  type HistoryOptions,
  isBankFundingMethod,
  isFundingMethod,
  isWithdrawalMethod,
  OnrampApiError,
  type OnrampClient,
  withdrawalArtwork,
} from '@morfi/onramp-sdk';
import {
  useFundingCurrency,
  useFundingHistoryNavigation,
  useHideFundingHistoryOperation,
  useOnrampHistory,
} from '@morfi/onramp-sdk/react';
import type { OnrampRealtime } from '@morfi/onramp-sdk/realtime';
import { useFormatter, useLocale, useTranslations } from 'next-intl';
import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { AppTabs } from '@/components/ui/app-tabs';
import { ArrowRight01Icon, Delete02Icon } from '@/components/ui/icon/glyphs';
import { SelectField } from '@/components/ui/select-field';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';
import { FundingChromeContext, useFundingChrome } from '../shared/funding-chrome';
import { FundingDialog } from '../shared/funding-dialog';
import { FundingError } from '../shared/funding-error';
import { useFundingError } from '../shared/use-funding-error';
import { FundingReceipt } from './funding-receipt';
import { useFundingStatusLabels } from './use-funding-status-labels';

interface FundingHistoryProps {
  client: OnrampClient;
  open: boolean;
  realtime?: OnrampRealtime | undefined;
  initialOperationId?: string;
  onResumeCryptoDeposit?: ((id: string) => void) | undefined;
  onResumeConnectedWallet?: ((id: string) => void) | undefined;
  origin?: 'deposit' | 'withdraw';
  onOpenChange(open: boolean): void;
  onReturnToPurchase?: (() => void) | undefined;
  onSignIn(): void;
}
const statuses: readonly NonNullable<HistoryOptions['status']>[] = [
  'created',
  'action_required',
  'awaiting_payment',
  'submission_unknown',
  'processing',
  'delivered',
  'failed',
  'cancelled',
  'refunded',
];

export function FundingHistory({
  client,
  open,
  realtime,
  initialOperationId,
  onResumeCryptoDeposit,
  onResumeConnectedWallet,
  origin = 'deposit',
  onOpenChange,
  onSignIn,
  onReturnToPurchase,
}: FundingHistoryProps): ReactNode {
  const t = useTranslations('onramp');
  const navigation = useFundingHistoryNavigation(client, open, initialOperationId, origin);
  const selected = navigation.page === 'receipt' ? navigation.receiptId : null;
  const [status, setStatus] = useState<HistoryOptions['status']>();
  const [category, setCategory] = useState<'onramp' | 'offramp'>(
    origin === 'withdraw' ? 'offramp' : 'onramp',
  );
  const chrome = useFundingChrome(t('history_title'));
  return (
    <FundingChromeContext.Provider value={chrome.report}>
      <FundingDialog
        open={open}
        onClose={(): void => onOpenChange(false)}
        onHistory={navigation.back}
        history
        chrome={{
          ...chrome,
          title: selected ? t('receipt_title') : t('history_title'),
          picker: false,
          hasBack: selected !== null,
          back: selected
            ? chrome.hasBack
              ? chrome.back
              : navigation.back
            : (): void => onOpenChange(false),
        }}
      >
        {open ? (
          selected ? (
            <FundingReceipt
              onReturnToPurchase={onReturnToPurchase}
              client={client}
              onSignIn={onSignIn}
              operationId={selected}
              {...(realtime ? { realtime } : {})}
              onBack={navigation.back}
            />
          ) : (
            <HistoryList
              client={client}
              {...(realtime ? { realtime } : {})}
              status={status}
              category={category}
              onStatusChange={setStatus}
              onCategoryChange={setCategory}
              onSelect={(operation: FundingHistoryOperation): void => {
                if (
                  operation.method === 'crypto_deposit' &&
                  operation.status !== 'delivered' &&
                  operation.status !== 'failed' &&
                  operation.status !== 'refunded' &&
                  onResumeCryptoDeposit
                )
                  onResumeCryptoDeposit(operation.id);
                else if (
                  operation.method === 'wallet_connect' &&
                  operation.status !== 'delivered' &&
                  operation.status !== 'failed' &&
                  operation.status !== 'refunded' &&
                  onResumeConnectedWallet
                )
                  onResumeConnectedWallet(operation.id);
                else navigation.openReceipt(operation.id, 'history');
              }}
              onSignIn={onSignIn}
            />
          )
        ) : null}
      </FundingDialog>
    </FundingChromeContext.Provider>
  );
}

function deferHistoryActivation(event: MouseEvent<HTMLButtonElement>): void {
  // Changing height on mouse-down can recenter the sheet under the pointer and dismiss on mouse-up.
  event.preventDefault();
}

function HistoryList({
  client,
  realtime,
  status,
  category,
  onStatusChange,
  onCategoryChange,
  onSelect,
  onSignIn,
}: {
  client: OnrampClient;
  realtime?: OnrampRealtime | undefined;
  status: HistoryOptions['status'];
  category: 'onramp' | 'offramp';
  onStatusChange(status: HistoryOptions['status']): void;
  onCategoryChange(category: 'onramp' | 'offramp'): void;
  onSelect(operation: FundingHistoryOperation): void;
  onSignIn(): void;
}): ReactNode {
  const rates = useExchangeRates();
  const selectedCurrency = useCurrencyStore((state) => state.currency);
  const currency = useFundingCurrency(selectedCurrency, rates.fundingFx);
  function withdrawalMoney(amountD6: string): string {
    return currency.status === 'ready' && currency.fx
      ? formatFiatUnits(
          fundingSettlementValue(amountD6, currency.currency, currency.decimals, currency.fx),
          currency.decimals,
          currency.currency,
          locale,
        )
      : t('amount_pending');
  }
  const t = useTranslations('onramp');
  const locale = useLocale();
  const formatter = useFormatter();
  const statusLabels = useFundingStatusLabels();
  const history = useOnrampHistory(client, status, realtime, category);
  useFundingError(history.error);
  const hide = useHideFundingHistoryOperation(client);
  useEffect(() => {
    if (!hide.error) return;
    logger.warn('onramp.history_hide_failed', {
      code: hide.error instanceof OnrampApiError ? hide.error.code : 'onramp_request_failed',
      ...(hide.error instanceof OnrampApiError
        ? { requestId: hide.error.requestId, status: hide.error.status }
        : {}),
    });
    showToast({
      id: TOAST_IDS.ONRAMP_HISTORY_HIDE,
      message: t('history_hide_failed'),
      variant: 'error',
    });
  }, [hide.error, t]);
  const items = history.isError ? [] : (history.data?.pages.flatMap((page) => page.data) ?? []);
  return (
    <div className="rr-block flex flex-col gap-4">
      <AppTabs.Root
        value={category}
        activationMode="manual"
        onValueChange={(value: string): void =>
          onCategoryChange(value === 'offramp' ? 'offramp' : 'onramp')
        }
      >
        <AppTabs.List variant="segmented" size="md" className="w-full">
          <AppTabs.Trigger
            value="onramp"
            onMouseDown={deferHistoryActivation}
            onClick={(): void => onCategoryChange('onramp')}
          >
            {t('history_onramp')}
          </AppTabs.Trigger>
          <AppTabs.Trigger
            value="offramp"
            onMouseDown={deferHistoryActivation}
            onClick={(): void => onCategoryChange('offramp')}
          >
            {t('history_offramp')}
          </AppTabs.Trigger>
        </AppTabs.List>
      </AppTabs.Root>
      {!history.isPending && !history.isError && (items.length > 0 || status !== undefined) ? (
        <SelectField
          label={t('status_filter')}
          value={status ?? 'all'}
          onValueChange={(value: string): void =>
            onStatusChange(statuses.find((item) => item === value))
          }
          items={[
            { value: 'all', label: t('all_statuses') },
            ...statuses.map((value) => ({ value, label: statusLabels[value] })),
          ]}
        />
      ) : null}
      {history.isPending ? (
        <output aria-label={t('loading')} className="flex flex-col gap-3">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-16 w-full" />
          ))}
        </output>
      ) : null}
      {history.isError ? (
        <FundingError
          error={history.error}
          onSignIn={onSignIn}
          onRetry={(): void => {
            void history.refetch();
          }}
        />
      ) : null}
      {!history.isPending && !history.isError && items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <OptimizedImage
            // ALLOW-NSFW-FALSE: approved bundled payment/status artwork, never user-uploaded media.
            nsfw={false}
            variant="thumbnail"
            // ALLOW-NO-PREVIEW: Bundled payment/status artwork is decorative, not expandable user media.
            preview={false}
            loadReveal="none"
            rounded=""
            imageClassName="object-contain"
            src="/assets/onramp/status-receipt.png"
            alt=""
            size="size-20"
            className="bg-transparent"
          />
          <h3 className="text-heading-sm">
            {status
              ? t('history_filtered_empty_title')
              : category === 'offramp'
                ? t('history_offramp_empty_title')
                : t('history_onramp_empty_title')}
          </h3>
          <p className="text-body text-foreground-secondary">
            {status
              ? t('history_filtered_empty_description')
              : category === 'offramp'
                ? t('history_offramp_empty_description')
                : t('history_onramp_empty_description')}
          </p>
        </div>
      ) : null}
      {items.length > 0 ? (
        <div className="divide-y divide-border-secondary">
          {items.map((operation: FundingHistoryOperation) => (
            <div
              key={operation.id}
              className={
                ['delivered', 'failed', 'cancelled', 'refunded'].includes(operation.status)
                  ? 'group/history-row grid grid-cols-[minmax(0,1fr)_0rem] items-center transition-[grid-template-columns] duration-fast ease-position hover:grid-cols-[minmax(0,1fr)_2.75rem] focus-within:grid-cols-[minmax(0,1fr)_2.75rem] motion-reduce:transition-none [@media(hover:none)]:grid-cols-[minmax(0,1fr)_2.75rem]'
                  : 'grid grid-cols-1 items-center'
              }
            >
              <AnimatedButton
                variant="tertiary"
                className="h-auto min-h-16 min-w-0 justify-start gap-3 rounded-item px-0 py-3 text-start hover:bg-transparent hover:shadow-none"
                onClick={(): void => onSelect(operation)}
                suffix={ArrowRight01Icon}
              >
                <OptimizedImage
                  // ALLOW-NSFW-FALSE: approved bundled payment/status artwork, never user-uploaded media.
                  nsfw={false}
                  variant="thumbnail"
                  // ALLOW-NO-PREVIEW: Bundled payment/status artwork is decorative, not expandable user media.
                  preview={false}
                  loadReveal="none"
                  rounded=""
                  imageClassName="object-contain"
                  src={`/assets/onramp/method-${operation.method === 'cex_connect' ? 'coinbase' : operation.method === 'wallet_connect' ? 'wallet' : operation.method === 'crypto_deposit' ? 'crypto' : operation.withdrawal && isWithdrawalMethod(operation.method) ? withdrawalArtwork[operation.method] : isFundingMethod(operation.method) ? fundingMethodArtwork[operation.method] : 'moonpay'}.png`}
                  alt=""
                  size="size-10"
                  className="shrink-0 bg-transparent object-contain"
                />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-body text-foreground-primary">
                    {operation.purchase
                      ? `${t('direct_purchase_title')} · ${operation.purchase.title}`
                      : t(
                          operation.method === 'cex_connect'
                            ? 'exchange_coinbase'
                            : operation.method === 'crypto_deposit'
                              ? 'crypto_deposit_title'
                              : operation.method === 'wallet_connect'
                                ? 'connected_wallet_title'
                                : isFundingMethod(operation.method) ||
                                    isWithdrawalMethod(operation.method)
                                  ? operation.method
                                  : 'hosted_payment_method',
                        )}
                  </span>
                  <span className="text-caption text-foreground-secondary">
                    {operation.purpose === 'direct_token_purchase' &&
                    operation.status === 'delivered' &&
                    operation.purchaseFulfillment?.state !== 'delivered'
                      ? t('direct_purchase_pending')
                      : isBankFundingMethod(operation.method) &&
                          operation.status === 'awaiting_payment'
                        ? t('bank_awaiting_transfer')
                        : statusLabels[operation.status]}{' '}
                    · {formatter.dateTime(new Date(operation.createdAt), { dateStyle: 'medium' })}
                    {isBankFundingMethod(operation.method) &&
                    ['awaiting_payment', 'processing'].includes(operation.status)
                      ? ` · ${t(operation.method === 'sepa' ? 'bank_arrival_estimate' : 'bank_arrival_unconfirmed')}`
                      : ''}
                  </span>
                </span>
                <span className="shrink-0 text-body text-foreground-primary">
                  {operation.withdrawal
                    ? withdrawalMoney(operation.withdrawal.amountD6)
                    : (fundingSourcePresentation(operation, locale) ?? t('amount_pending'))}
                </span>
              </AnimatedButton>
              {['delivered', 'failed', 'cancelled', 'refunded'].includes(operation.status) ? (
                <div className="min-w-0 overflow-hidden">
                  <AnimatedButton
                    variant="tertiary"
                    aria-label={t('history_hide')}
                    title={t('history_hide')}
                    prefix={Delete02Icon}
                    disabled={hide.isPending}
                    loading={hide.isPending && hide.variables === operation.id}
                    onClick={(): void =>
                      hide.mutate(operation.id, {
                        onSuccess: (): void =>
                          showToast({
                            id: TOAST_IDS.ONRAMP_HISTORY_HIDE,
                            message: t('history_hide_success'),
                            variant: 'success',
                          }),
                      })
                    }
                    className="size-11 opacity-0 transition-opacity duration-fast ease-default group-hover/history-row:opacity-100 group-focus-within/history-row:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100"
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {history.hasNextPage ? (
        <AnimatedButton
          variant="outline"
          className="w-full"
          loading={history.isFetchingNextPage}
          onClick={(): void => {
            void history.fetchNextPage();
          }}
        >
          {t('load_more')}
        </AnimatedButton>
      ) : null}
    </div>
  );
}
